import { GoogleGenAI } from '@google/genai';
import type { VercelResponse } from '@vercel/node';

// Get API client - must be called inside handler to access runtime env vars
export function getAIClient(): GoogleGenAI {
  const apiKey = process.env.GEMINI_API_KEY;
  if (!apiKey) {
    throw new Error('GEMINI_API_KEY is not configured in environment variables');
  }
  return new GoogleGenAI({ apiKey });
}

// Claude API helper functions
export function hasClaudeKey(): boolean {
  return !!process.env.CLAUDE_API_KEY;
}

export async function callClaudeAPI(options: {
  model?: string;
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  maxTokens?: number;
  system?: string;
}): Promise<string> {
  const apiKey = process.env.CLAUDE_API_KEY;
  if (!apiKey) {
    throw new Error('CLAUDE_API_KEY is not configured');
  }

  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
    },
    body: JSON.stringify({
      model: options.model || 'claude-sonnet-4-20250514', // Default to Claude Sonnet 4
      max_tokens: options.maxTokens || 4096,
      messages: options.messages,
      system: options.system,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Claude API error: ${response.status} - ${errorText}`);
  }

  const data = await response.json();
  return data.content[0]?.text || '';
}

export async function* streamClaudeAPI(options: {
  model?: string;
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  maxTokens?: number;
  system?: string;
}): AsyncGenerator<string, void, unknown> {
  const apiKey = process.env.CLAUDE_API_KEY;
  if (!apiKey) {
    throw new Error('CLAUDE_API_KEY is not configured');
  }

  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
    },
    body: JSON.stringify({
      model: options.model || 'claude-sonnet-4-20250514',
      max_tokens: options.maxTokens || 4096,
      messages: options.messages,
      system: options.system,
      stream: true,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Claude API error: ${response.status} - ${errorText}`);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response body');

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const data = line.slice(6);
        if (data === '[DONE]') return;
        
        try {
          const parsed = JSON.parse(data);
          if (parsed.type === 'content_block_delta' && parsed.delta?.text) {
            yield parsed.delta.text;
          }
        } catch (e) {
          // Skip invalid JSON
        }
      }
    }
  }
}

// Alias for consistency with endpoint code
export const getAI = getAIClient;

// For Edge functions (chat.ts), we need a client that works at module level
// but we'll handle the error more gracefully
let cachedAI: GoogleGenAI | null = null;

export const getEdgeAIClient = (): GoogleGenAI => {
  if (cachedAI) return cachedAI;
  
  const apiKey = process.env.GEMINI_API_KEY;
  if (!apiKey) {
    throw new Error('GEMINI_API_KEY is not configured. Please add it to your Vercel environment variables.');
  }
  
  cachedAI = new GoogleGenAI({ apiKey });
  return cachedAI;
};

// Legacy export for backwards compatibility with chat.ts
export const ai = {
  get models() {
    return getEdgeAIClient().models;
  }
};

// Validate API key is available (checks at runtime)
export function validateApiKey(res: VercelResponse): boolean {
  const apiKey = process.env.GEMINI_API_KEY;
  if (!apiKey) {
    res.status(500).json({ 
      error: 'GEMINI_API_KEY is not configured in environment variables. Please add it in Vercel dashboard under Settings > Environment Variables.' 
    });
    return false;
  }
  return true;
}

// A helper to send structured error responses
export function handleError(res: VercelResponse, error: unknown, message = 'An unknown error occurred.') {
  console.error('API Error:', error);
  const errorMessage = error instanceof Error ? error.message : message;
  res.status(500).json({ error: errorMessage });
}

// Helper to extract JSON from a string that might be wrapped in markdown.
export const extractJsonFromString = (text: string): string => {
  const jsonBlockMatch = text.match(/```json\s*([\s\S]*?)\s*```/);
  if (jsonBlockMatch && jsonBlockMatch[1]) {
    return jsonBlockMatch[1].trim();
  }
  const genericBlockMatch = text.match(/```\s*([\s\S]*?)\s*```/);
  if (genericBlockMatch && genericBlockMatch[1]) {
    return genericBlockMatch[1].trim();
  }
  const firstBrace = text.indexOf('{');
  const lastBrace = text.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace > firstBrace) {
    return text.substring(firstBrace, lastBrace + 1).trim();
  }
  return text.trim();
};
